import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Table, Button, Badge, Modal, Form } from 'react-bootstrap';
import { Users, Folder, BarChart3, Trash2, Edit } from 'lucide-react';
import toast from 'react-hot-toast';

import apiService from '../services/api';
import { useAuth } from '../contexts/AuthContext';

const AdminPanel = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState({});
  const [users, setUsers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [activeTab, setActiveTab] = useState('stats');
  const [showUserModal, setShowUserModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user?.role !== 'administrator') {
      toast.error('Access denied. Administrator role required.');
      return;
    }
    loadAdminData();
  }, [user]);

  const loadAdminData = async () => {
    try {
      const [statsData, usersData, projectsData] = await Promise.all([
        apiService.getAdminStats(),
        apiService.getAllUsers(),
        apiService.getAllProjects(),
      ]);

      setStats(statsData);
      setUsers(usersData);
      setProjects(projectsData);
    } catch (error) {
      console.error('Error loading admin data:', error);
      toast.error('Failed to load admin data');
    } finally {
      setLoading(false);
    }
  };

  const handleEditUser = (user) => {
    setSelectedUser(user);
    setShowUserModal(true);
  };

  const handleUpdateUser = async (e) => {
    e.preventDefault();
    if (!selectedUser) return;

    const formData = new FormData(e.target);
    const userData = {
      full_name: formData.get('full_name'),
      email: formData.get('email'),
      role: formData.get('role'),
      is_active: formData.get('is_active') === 'true',
    };

    try {
      const updatedUser = await apiService.updateUser(selectedUser.id, userData);
      setUsers(prev => prev.map(u => u.id === selectedUser.id ? updatedUser : u));
      setShowUserModal(false);
      setSelectedUser(null);
      toast.success('User updated successfully');
    } catch (error) {
      console.error('Error updating user:', error);
      toast.error('Failed to update user');
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;

    try {
      await apiService.deleteUser(userId);
      setUsers(prev => prev.filter(u => u.id !== userId));
      toast.success('User deleted successfully');
    } catch (error) {
      console.error('Error deleting user:', error);
      toast.error('Failed to delete user');
    }
  };

  const handleDeleteProject = async (projectId) => {
    if (!window.confirm('Are you sure you want to delete this project?')) return;

    try {
      await apiService.deleteProjectAsAdmin(projectId);
      setProjects(prev => prev.filter(p => p.id !== projectId));
      toast.success('Project deleted successfully');
    } catch (error) {
      console.error('Error deleting project:', error);
      toast.error('Failed to delete project');
    }
  };

  if (user?.role !== 'administrator') {
    return (
      <Container className="mt-5 text-center">
        <h3>Access Denied</h3>
        <p>You need administrator privileges to access this page.</p>
      </Container>
    );
  }

  if (loading) {
    return <div className="text-center mt-5">Loading...</div>;
  }

  return (
    <Container className="mt-4">
      <Row className="mb-4">
        <Col>
          <h2>Admin Panel</h2>
          <p className="text-muted">Manage users, projects, and system settings</p>
        </Col>
      </Row>

      {/* Navigation Tabs */}
      <Row className="mb-4">
        <Col>
          <div className="d-flex gap-2">
            <Button
              variant={activeTab === 'stats' ? 'primary' : 'outline-primary'}
              onClick={() => setActiveTab('stats')}
            >
              <BarChart3 size={16} className="me-1" />
              Statistics
            </Button>
            <Button
              variant={activeTab === 'users' ? 'primary' : 'outline-primary'}
              onClick={() => setActiveTab('users')}
            >
              <Users size={16} className="me-1" />
              Users
            </Button>
            <Button
              variant={activeTab === 'projects' ? 'primary' : 'outline-primary'}
              onClick={() => setActiveTab('projects')}
            >
              <Folder size={16} className="me-1" />
              Projects
            </Button>
          </div>
        </Col>
      </Row>

      {/* Statistics Tab */}
      {activeTab === 'stats' && (
        <Row>
          <Col md={3}>
            <Card className="text-center">
              <Card.Body>
                <Users size={48} className="text-primary mb-3" />
                <h3>{stats.total_users}</h3>
                <p className="text-muted">Total Users</p>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="text-center">
              <Card.Body>
                <Users size={48} className="text-success mb-3" />
                <h3>{stats.active_users}</h3>
                <p className="text-muted">Active Users</p>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="text-center">
              <Card.Body>
                <Folder size={48} className="text-info mb-3" />
                <h3>{stats.total_projects}</h3>
                <p className="text-muted">Total Projects</p>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="text-center">
              <Card.Body>
                <Folder size={48} className="text-warning mb-3" />
                <h3>{stats.active_projects}</h3>
                <p className="text-muted">Active Projects</p>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}

      {/* Users Tab */}
      {activeTab === 'users' && (
        <Row>
          <Col>
            <Card>
              <Card.Header>
                <h5 className="mb-0">User Management</h5>
              </Card.Header>
              <Card.Body>
                <Table responsive striped>
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Email</th>
                      <th>Username</th>
                      <th>Role</th>
                      <th>Status</th>
                      <th>Created</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((user) => (
                      <tr key={user.id}>
                        <td>{user.full_name || 'N/A'}</td>
                        <td>{user.email}</td>
                        <td>{user.username}</td>
                        <td>
                          <Badge bg="secondary">
                            {user.role.replace('_', ' ')}
                          </Badge>
                        </td>
                        <td>
                          <Badge bg={user.is_active ? 'success' : 'danger'}>
                            {user.is_active ? 'Active' : 'Inactive'}
                          </Badge>
                        </td>
                        <td>{new Date(user.created_at).toLocaleDateString()}</td>
                        <td>
                          <Button
                            variant="outline-primary"
                            size="sm"
                            className="me-2"
                            onClick={() => handleEditUser(user)}
                          >
                            <Edit size={14} />
                          </Button>
                          <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={() => handleDeleteUser(user.id)}
                            disabled={user.id === user?.id}
                          >
                            <Trash2 size={14} />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}

      {/* Projects Tab */}
      {activeTab === 'projects' && (
        <Row>
          <Col>
            <Card>
              <Card.Header>
                <h5 className="mb-0">Project Management</h5>
              </Card.Header>
              <Card.Body>
                <Table responsive striped>
                  <thead>
                    <tr>
                      <th>Title</th>
                      <th>Description</th>
                      <th>Owner</th>
                      <th>Status</th>
                      <th>Created</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {projects.map((project) => (
                      <tr key={project.id}>
                        <td>{project.title}</td>
                        <td>{project.description || 'No description'}</td>
                        <td>Owner ID: {project.owner_id}</td>
                        <td>
                          <Badge bg={project.is_active ? 'success' : 'danger'}>
                            {project.is_active ? 'Active' : 'Inactive'}
                          </Badge>
                        </td>
                        <td>{new Date(project.created_at).toLocaleDateString()}</td>
                        <td>
                          <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={() => handleDeleteProject(project.id)}
                          >
                            <Trash2 size={14} />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}

      {/* Edit User Modal */}
      <Modal show={showUserModal} onHide={() => setShowUserModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Edit User</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleUpdateUser}>
          <Modal.Body>
            {selectedUser && (
              <>
                <Form.Group className="mb-3">
                  <Form.Label>Full Name</Form.Label>
                  <Form.Control
                    type="text"
                    name="full_name"
                    defaultValue={selectedUser.full_name}
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Email</Form.Label>
                  <Form.Control
                    type="email"
                    name="email"
                    defaultValue={selectedUser.email}
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Role</Form.Label>
                  <Form.Select name="role" defaultValue={selectedUser.role}>
                    <option value="contributor">Contributor</option>
                    <option value="reviewer">Reviewer</option>
                    <option value="project_owner">Project Owner</option>
                    <option value="administrator">Administrator</option>
                  </Form.Select>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Status</Form.Label>
                  <Form.Select name="is_active" defaultValue={selectedUser.is_active.toString()}>
                    <option value="true">Active</option>
                    <option value="false">Inactive</option>
                  </Form.Select>
                </Form.Group>
              </>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowUserModal(false)}>
              Cancel
            </Button>
            <Button type="submit" variant="primary">
              Update User
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </Container>
  );
};

export default AdminPanel;